import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Button
} from '@mui/material'
import {
  Search,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  Receipt,
  PictureAsPdf,
  CheckCircle,
  Schedule,
  Warning,
  FilterList,
  Clear,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore
} from '@mui/icons-material'

import { useInvoices, useUpdateInvoiceStatus, useDeleteInvoice } from '../../hooks/useInvoices'
import { useClients } from '../../hooks/useClients'
import { formatCurrency, formatDate, formatInvoiceStatus, formatDaysUntil } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../Auth/RoleGuard'
import PdfViewer from '../Common/PdfViewer'
import { invoicesAPI } from '../../utils/api'

const InvoicesTable = ({ onEdit, onView }) => {
  const { isAdmin } = useAuthStore()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [sortBy, setSortBy] = useState('dueDate')
  const [sortOrder, setSortOrder] = useState('desc')
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null)
  const [currentPdfTitle, setCurrentPdfTitle] = useState('')

  const updateStatusMutation = useUpdateInvoiceStatus()
  const deleteInvoiceMutation = useDeleteInvoice()

  // Get clients for filter
  const { data: clientsData } = useClients({ page: 1, limit: 100 })

  // Query parameters
  const queryParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: search.trim(),
    status: statusFilter,
    clientId: clientFilter,
    sortBy: sortBy,
    sortOrder: sortOrder
  }), [page, rowsPerPage, search, statusFilter, clientFilter, sortBy, sortOrder])

  const { data, isLoading, error, refetch } = useInvoices(queryParams)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value)
    setPage(0)
  }

  const handleClientFilterChange = (event) => {
    setClientFilter(event.target.value)
    setPage(0)
  }

  const handleClearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setClientFilter('')
    setPage(0)
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to ascending
      setSortBy(column)
      setSortOrder('asc')
    }
    setPage(0) // Reset to first page when sorting
  }

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <UnfoldMore fontSize="small" sx={{ opacity: 0.5 }} />
    }
    return sortOrder === 'asc' 
      ? <ArrowUpward fontSize="small" />
      : <ArrowDownward fontSize="small" />
  }

  // Sortable header component
  const SortableHeader = ({ column, children, align = 'left', sortable = true }) => {
    if (!sortable) {
      return <TableCell align={align}>{children}</TableCell>
    }

    return (
      <TableCell 
        align={align}
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={() => handleSort(column)}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          gap: 0.5
        }}>
          {children}
          {getSortIcon(column)}
        </Box>
      </TableCell>
    )
  }

  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget)
    setSelectedInvoice(invoice)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedInvoice(null)
  }

  const handleEdit = () => {
    if (selectedInvoice && onEdit) {
      onEdit(selectedInvoice)
    }
    handleMenuClose()
  }

  const handleView = () => {
    if (selectedInvoice && onView) {
      onView(selectedInvoice)
    }
    handleMenuClose()
  }

  const handleViewPdf = async () => {
    if (selectedInvoice?.pdfKey) {
      try {
        const response = await invoicesAPI.getPdfUrl(selectedInvoice.id)
        if (response.data.signedUrl) {
          setCurrentPdfUrl(response.data.signedUrl)
          setCurrentPdfTitle(`Factura ${selectedInvoice.invoiceNumber}`)
          setPdfViewerOpen(true)
        }
      } catch (error) {
        console.error('Error getting PDF URL:', error)
      }
    }
    handleMenuClose()
  }

  // Direct PDF handler for table button (doesn't use selectedInvoice)
  const handleDirectViewPdf = async (invoice) => {
    if (invoice?.pdfKey) {
      try {
        const response = await invoicesAPI.getPdfUrl(invoice.id)
        if (response.data.signedUrl) {
          setCurrentPdfUrl(response.data.signedUrl)
          setCurrentPdfTitle(`Factura ${invoice.invoiceNumber}`)
          setSelectedInvoice(invoice) // Set for the modal
          setPdfViewerOpen(true)
        }
      } catch (error) {
        console.error('Error getting PDF URL:', error)
      }
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (selectedInvoice) {
      try {
        await updateStatusMutation.mutateAsync({
          id: selectedInvoice.id,
          status: newStatus
        })
        refetch()
      } catch (error) {
        console.error('Error updating status:', error)
      }
    }
    handleMenuClose()
  }

  const handleDelete = async () => {
    if (selectedInvoice) {
      try {
        await deleteInvoiceMutation.mutateAsync(selectedInvoice.id)
        refetch()
      } catch (error) {
        console.error('Error deleting invoice:', error)
      }
    }
    handleMenuClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAGADA': return 'success'
      case 'POR_COBRAR': return 'warning'
      case 'VENCIDA': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAGADA': return <CheckCircle fontSize="small" />
      case 'POR_COBRAR': return <Schedule fontSize="small" />
      case 'VENCIDA': return <Warning fontSize="small" />
      default: return <Receipt fontSize="small" />
    }
  }

  const getDueDateColor = (dueDate, status) => {
    if (status === 'PAGADA') return 'text.primary'
    
    const now = new Date()
    const due = new Date(dueDate)
    const daysUntilDue = Math.ceil((due - now) / (1000 * 60 * 60 * 24))
    
    if (daysUntilDue < 0) return 'error.main' // Overdue
    if (daysUntilDue <= 7) return 'warning.main' // Due soon
    return 'text.primary'
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton size="small" onClick={() => refetch()}>
            <Search />
          </IconButton>
        }
      >
        Error cargando facturas: {error.message}
      </Alert>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filters Bar */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar por número, cliente o descripción..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="POR_COBRAR">Por Cobrar</MenuItem>
                <MenuItem value="PAGADA">Pagada</MenuItem>
                <MenuItem value="VENCIDA">Vencida</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cliente</InputLabel>
              <Select
                value={clientFilter}
                onChange={handleClientFilterChange}
                label="Cliente"
              >
                <MenuItem value="">Todos los clientes</MenuItem>
                {clientsData?.clients?.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                size="small"
              >
                Limpiar
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                size="small"
              >
                Filtros
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <SortableHeader column="invoiceNumber">Factura</SortableHeader>
              <SortableHeader column="clientName">Cliente</SortableHeader>
              <SortableHeader column="description" sortable={false}>Descripción</SortableHeader>
              <SortableHeader column="issueDate" align="center">Emisión</SortableHeader>
              <SortableHeader column="dueDate" align="center">Vencimiento</SortableHeader>
              <SortableHeader column="totalAmount" align="right">Monto</SortableHeader>
              <SortableHeader column="status" align="center">Estado</SortableHeader>
              <SortableHeader column="" align="center" sortable={false}>PDF</SortableHeader>
              <SortableHeader column="" align="center" sortable={false}>Acciones</SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={150} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                  <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                </TableRow>
              ))
            ) : data?.invoices?.length > 0 ? (
              data.invoices.map((invoice) => (
                <TableRow 
                  key={invoice.id} 
                  hover
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  {/* Invoice Number */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>

                  {/* Client */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {invoice.client?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invoice.client?.rut}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Description */}
                  <TableCell>
                    <Tooltip title={invoice.description}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {invoice.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Issue Date */}
                  <TableCell align="center">
                    <Typography variant="body2">
                      {formatDate(invoice.issueDate)}
                    </Typography>
                  </TableCell>

                  {/* Due Date */}
                  <TableCell align="center">
                    <Typography 
                      variant="body2"
                      color={getDueDateColor(invoice.dueDate, invoice.status)}
                      fontWeight={invoice.status !== 'PAGADA' ? 600 : 400}
                    >
                      {formatDate(invoice.dueDate)}
                    </Typography>
                    {invoice.status !== 'PAGADA' && (
                      <Typography 
                        variant="caption" 
                        color={getDueDateColor(invoice.dueDate, invoice.status)}
                        display="block"
                      >
                        {formatDaysUntil(invoice.dueDate)}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Amount */}
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(invoice.totalAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Neto: {formatCurrency(invoice.netAmount)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell align="center">
                    <Chip
                      label={formatInvoiceStatus(invoice.visualStatus || invoice.status)}
                      color={getStatusColor(invoice.visualStatus || invoice.status)}
                      size="small"
                      variant="outlined"
                      icon={getStatusIcon(invoice.visualStatus || invoice.status)}
                    />
                  </TableCell>

                  {/* PDF */}
                  <TableCell align="center">
                    {invoice.pdfKey ? (
                      <Tooltip title="Ver PDF">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDirectViewPdf(invoice)
                          }}
                          color="primary"
                        >
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Sin PDF">
                        <IconButton size="small" disabled>
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip title="Más opciones">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, invoice)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      No se encontraron facturas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter || clientFilter 
                        ? 'Intenta con otros filtros de búsqueda' 
                        : 'Aún no hay facturas registradas'
                      }
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver detalles</ListItemText>
        </MenuItem>

        {selectedInvoice?.pdfKey && (
          <MenuItem onClick={handleViewPdf}>
            <ListItemIcon>
              <PictureAsPdf fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver PDF</ListItemText>
          </MenuItem>
        )}

        <RoleGuard requireAdmin fallback={null}>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>

          {/* Status change options */}
          {selectedInvoice?.status !== 'PAGADA' && (
            <MenuItem onClick={() => handleStatusChange('PAGADA')}>
              <ListItemIcon>
                <CheckCircle fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Marcar como Pagada</ListItemText>
            </MenuItem>
          )}

          {selectedInvoice?.status !== 'POR_COBRAR' && (
            <MenuItem onClick={() => handleStatusChange('POR_COBRAR')}>
              <ListItemIcon>
                <Schedule fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>Marcar como Por Cobrar</ListItemText>
            </MenuItem>
          )}

          {/* VENCIDA is now calculated automatically - no manual option */}

          <MenuItem 
            onClick={handleDelete}
            disabled={deleteInvoiceMutation.isLoading}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Eliminar</ListItemText>
          </MenuItem>
        </RoleGuard>
      </Menu>

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={currentPdfUrl}
        title={currentPdfTitle}
        fileName={`${selectedInvoice?.invoiceNumber || 'factura'}.pdf`}
      />
    </Paper>
  )
}

export default InvoicesTable
